import { Injectable } from '@angular/core';
import {
  HttpEvent,
  HttpInterceptor,
  HttpHandler,
  HttpRequest,
  HttpErrorResponse,
} from '@angular/common/http';
import { Observable, throwError, timer } from 'rxjs';
import { catchError, retry } from 'rxjs/operators';
import { API } from '../constant/api';

export const MAX_RETRIES = 3;
export const RETRY_DELAY_MS = 1000;

// Routes that should not be retried
const NO_RETRY_ROUTES = [API.MOBILECHECKLOGIN];

@Injectable()
export class RetryInterceptor implements HttpInterceptor {
  private shouldSkipRetry(request: HttpRequest<unknown>): boolean {
    return NO_RETRY_ROUTES.some((route) => request.url.includes(route));
  }

  intercept(
    request: HttpRequest<unknown>,
    next: HttpHandler
  ): Observable<HttpEvent<unknown>> {
    // Skip retry for specific routes
    if (this.shouldSkipRetry(request)) {
      return next.handle(request).pipe(
        catchError((error: HttpErrorResponse | Error) => {
          console.error(
            `[RetryInterceptor] Request to ${request.urlWithParams} failed (no retry):`,
            error
          );
          return throwError(() => error);
        })
      );
    }

    return next.handle(request).pipe(
      retry({
        count: MAX_RETRIES,
        delay: (error: HttpErrorResponse | Error, retryCount: number) => {
          if (error instanceof HttpErrorResponse && error.status === 401) {
            console.warn(
              `[RetryInterceptor] Request to ${request.urlWithParams} failed with 401. Not retrying.`
            );
            return throwError(() => error); // This will stop the retry and propagate the 401 error
          }
          // Calculate exponential backoff delay
          const delayTime = RETRY_DELAY_MS * Math.pow(2, retryCount - 1);

          console.log(
            `[RetryInterceptor] Attempt ${retryCount}/${MAX_RETRIES}: Retrying request to ${
              request.urlWithParams
            } in ${delayTime}ms. Error: ${(error as Error).message}`
          );

          // Always return timer for any error
          return timer(delayTime);
        },
      }),
      catchError((error: HttpErrorResponse | Error) => {
        // This will catch errors after all retries are exhausted
        console.error(
          `[RetryInterceptor] Request to ${request.urlWithParams} failed after ${MAX_RETRIES} attempts:`,
          error
        );
        return throwError(() => error);
      })
    );
  }
}
